import { map, of } from 'rxjs';
import {
  readEntitiesByIDs,
  watchEntitiesByIDs,
  readAllEntities,
  watchAllEntities,
} from '@adornis/bh-util/server/entity-reads.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { type NarrowedEntity, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { getCurrentUserServer } from '@adornis/bh-util/util.js';
import { getParticipationsByID } from '@adornis/buchungsheld-db/api/participation-helper.js';
import { getStammdatenByUserAndInstitution } from '@adornis/buchungsheld-db/api/stammdaten.js';
import { Participation } from '@adornis/buchungsheld-db/entities/Participation.js';
import { notifyBestaetigungWorkshopRegistrierung } from '@adornis/buchungsheld-db/notification/bestaetigung-workshop-registrierung.js';
import { printComponent } from '@adornis/buchungsheld-db/print-with-user-session.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { BHDemoOrderNr } from '../payment/BHDemoOrderNr.js';
import { DemoStammdaten } from '../stammdaten/demo-stammdaten-plugin.js';
import { DemoInvoice, getProductPrice } from '../payment/demo-payment-plugin.js';
import { createPaymentFromParticipations } from '../payment/helper.js';

export const paymentPluginDemoPay = registerMutation({
  type: () => String,
  operationName: 'paymentPluginDemoPay',
  params: [
    { type: () => String, name: 'institutionID' },
    { type: () => [Participation], name: 'participationIds' },
  ],
  resolve: (institutionID: string, _participations: NarrowedEntity<Participation, { _id: 1; productID: 1 }>[]) => {
    return async () => {
      const participations = await getParticipationsByID(_participations.map(pa => pa._id))({
        _id: 1,
        userID: 1,
        user: {
          _id: 1,
          email: 1,
          firstName: 1,
          lastName: 1,
          title: 1,
          username: 1,
          whatsappNotification: 1,
          whatsappPhone: 1,
          suspentions: { _id: 1, institutionID: 1 },
          receivesNewsletter: 1,
        },
        productID: 1,
        createdAt: 1,
        updatedAt: 1,
        state: 1,
        isNoShow: 1,
        signoffDate: 1,
        registrationDate: 1,
        fulfilledParticipationRequirements: 1,
        lastStateChangeWasInitiatedByAdmin: 1,
        currentPayment: { status: 1, participationIDs: 1, orderNr: { year: 1, nr: 1 }, amount: 1, discount: 1 },
        productPriceForUser: { unitAmount: 1, currency: 1 },
        product: {
          _class: 1,
          _id: 1,
          name: 1,
          description: 1,
          translatedDescriptionHTML: { _class: 1, langKey: 1, text: 1 },
          sortingWeight: 1,
          hasPrice: 1,
          categoryID: 1,
          institutionID: 1,
          timeframeID: 1,
          attachedCertificate: { _class: 1, certificateID: 1, exportDate: 1 },
          publishDate: 1,
          topic: 1,
          language: 1,
          presenceType: 1,
          isCanceled: 1,
          ...{
            appointments: { _class: 1, _id: 1, start: 1, end: 1, isNonBlocking: 1, location: 1, name: 1 },
            capacity: 1,
            hideCapacity: 1,
          },
        },
      });

      const user = await getCurrentUserServer();
      if (!user) throw new Error('user should be loggedIn');

      // create Payment
      const stammdaten = await getStammdatenByUserAndInstitution(user._id, institutionID)(DemoStammdaten.allFields);
      if (!stammdaten) throw new Error('stammdaten not found');

      // send of mail per participation
      const paymentIDs = [] as string[];
      for (const participation of participations) {
        const participations = [participation];
        const payment = await createPaymentFromParticipations(institutionID, participations, getProductPrice);
        ((payment.orderNr = await BHDemoOrderNr.create(institutionID)), (payment.userReference.userID = user._id));

        const paymentID = await payment.create();
        logger.info({ paymentID }, '(demo) payment created');
        paymentIDs.push(paymentID);

        // send Mails
        await notifyBestaetigungWorkshopRegistrierung(institutionID, user, participations);

        // create Invoice
        const invoiceID = A.getGloballyUniqueID();
        const createInvoice = async (component: string, filenameType: string) => {
          const buffer = await printComponent(component, {
            user,
            timeout: 25 * 1000,
            queryParams: { payment: payment._id },
          });

          return await AdornisFile.createFromBuffer(buffer, `invoice-demo-${filenameType}-${invoiceID}.pdf`);
        };

        const invoiceCustomer = await createInvoice('demo-invoice-customer', 'customer');
        const invoiceInternal = await createInvoice('demo-invoice-internal', 'internal');

        const invoice = new DemoInvoice({
          _id: invoiceID,
          paymentID,
          pdfID: invoiceCustomer._id,
          pdfInternalID: invoiceInternal._id,
          userID: user._id,
          institutionID,
        });
        await invoice.create();
      }

      return paymentIDs;
    };
  },
});

export const getDemoInvoicesByIDs = registerQuery({
  operationName: 'getDemoInvoicesByIDs',
  type: () => [DemoInvoice],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<DemoInvoice>>(gqlFields: SelSet) =>
      readEntitiesByIDs(DemoInvoice, ids, gqlFields),
});

export const subscribeDemoInvoicesByIDs = registerSubscription({
  operationName: 'subscribeDemoInvoicesByIDs',
  type: () => [DemoInvoice],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<DemoInvoice>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(DemoInvoice, ids, gqlFields),
});

export const getAllDemoInvoices = registerQuery({
  operationName: 'getAllDemoInvoices',
  type: () => [DemoInvoice],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<DemoInvoice>>(gqlFields: SelSet) =>
      readAllEntities(DemoInvoice, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllDemoInvoices = registerSubscription({
  operationName: 'subscribeAllDemoInvoices',
  type: () => [DemoInvoice],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<DemoInvoice>>(gqlFields: SelSet) =>
      watchAllEntities(DemoInvoice, gqlFields, { skip, limit, excludeIDs }),
});

export const getDemoInvoiceByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<DemoInvoice>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getDemoInvoicesByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeDemoInvoiceByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<DemoInvoice>>(gqlFields: SelSet) =>
    id ? subscribeDemoInvoicesByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
